import { buildChildrenMap, getChildren, getInheritedTimeline } from './taskHierarchyUtils';
import { formatDuration, MINUTES_PER_DAY, normalizeTaskStatus } from './taskSchedulingUtils';

const toValidDate = value => {
    if (!value) return null;
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }
    const str = String(value);
    const dateOnly = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateOnly) {
        const date = new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
        return Number.isNaN(date.getTime()) ? null : date;
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const pad2 = n => String(n).padStart(2, '0');

export const formatGroupDate = date => {
    if (!date) return '';
    const parsed = date instanceof Date ? date : toValidDate(date);
    if (!parsed) return '';
    return `${pad2(parsed.getDate())}/${pad2(parsed.getMonth() + 1)}/${String(parsed.getFullYear()).slice(-2)}`;
};

const durationMinutesOf = value => {
    if (value == null || value === '') return null;
    const minutes = Number(value);
    return Number.isNaN(minutes) ? null : minutes;
};

const calendarDaysBetween = (start, end) => {
    if (!start || !end) return null;
    const diffMs = end.getTime() - start.getTime();
    if (diffMs <= 0) return null;
    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
};

const formatCalendarDays = days => {
    if (days == null || days <= 0) return '';
    return `${days} day${days === 1 ? '' : 's'}`;
};

/**
 * Group header rollup using the same timeline/duration values the table shows.
 * Parents without their own dates still contribute via child rollups.
 * Empty duration falls back to calendar days between start and end.
 */
export const getGroupScheduleSummary = (tasks = [], allTasks = tasks) => {
    const groupTasks = Array.isArray(tasks) ? tasks : [];
    const lookupTasks = Array.isArray(allTasks) && allTasks.length ? allTasks : groupTasks;
    const groupIds = new Set(groupTasks.map(task => task?.id).filter(Boolean));
    const childrenMap = buildChildrenMap(lookupTasks);

    let earliestStart = null;
    let latestEnd = null;
    let totalMinutes = 0;
    let totalCalendarDays = 0;
    let hasDurationMinutes = false;

    groupTasks.forEach(task => {
        if (!task) return;
        if (normalizeTaskStatus(task.status) === 'CANCELLED') return;

        const inheritedTimeline = getInheritedTimeline(task, lookupTasks, childrenMap);
        const start = toValidDate(task.start_date) || toValidDate(inheritedTimeline.start_date);
        const end = toValidDate(task.end_date) || toValidDate(inheritedTimeline.end_date);
        if (start && (!earliestStart || start < earliestStart)) earliestStart = start;
        if (end && (!latestEnd || end > latestEnd)) latestEnd = end;

        const children = getChildren(task.id, lookupTasks, childrenMap);
        if (children.some(child => groupIds.has(child.id))) return;

        const addLeafDuration = node => {
            if (!node || normalizeTaskStatus(node.status) === 'CANCELLED') return;
            const nodeChildren = getChildren(node.id, lookupTasks, childrenMap);
            if (nodeChildren.length > 0) {
                nodeChildren.forEach(addLeafDuration);
                return;
            }
            const minutes = durationMinutesOf(node.duration);
            if (minutes != null) {
                totalMinutes += minutes;
                hasDurationMinutes = true;
                return;
            }
            const nodeTimeline = getInheritedTimeline(node, lookupTasks, childrenMap);
            const days = calendarDaysBetween(
                toValidDate(node.start_date) || toValidDate(nodeTimeline.start_date),
                toValidDate(node.end_date) || toValidDate(nodeTimeline.end_date),
            );
            if (days != null) totalCalendarDays += days;
        };

        addLeafDuration(task);
    });

    const startLabel = formatGroupDate(earliestStart);
    const endLabel = formatGroupDate(latestEnd);
    let timelineLabel = '';
    if (startLabel && endLabel) timelineLabel = `${startLabel} - ${endLabel}`;
    else if (startLabel) timelineLabel = startLabel;
    else if (endLabel) timelineLabel = endLabel;

    let durationLabel = '';
    let durationMinutes = null;
    if (hasDurationMinutes) {
        durationMinutes = totalMinutes + totalCalendarDays * MINUTES_PER_DAY;
        durationLabel = formatDuration(durationMinutes);
    } else if (totalCalendarDays > 0) {
        durationMinutes = totalCalendarDays * MINUTES_PER_DAY;
        durationLabel = formatCalendarDays(totalCalendarDays);
    }

    return {
        startDate: earliestStart,
        endDate: latestEnd,
        durationMinutes,
        timelineLabel,
        durationLabel,
    };
};

export const formatGroupHeaderTitle = (groupName, tasks = [], allTasks = tasks) => {
    const count = tasks?.length || 0;
    const parts = [`${groupName} (${count} ${count === 1 ? 'task' : 'tasks'})`];
    const summary = getGroupScheduleSummary(tasks, allTasks);
    if (summary.timelineLabel) parts.push(summary.timelineLabel);
    if (summary.durationLabel) parts.push(summary.durationLabel);
    return parts.join(' · ');
};
